import React, { useCallback, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import block from 'bem-cn'
import { motion } from 'framer-motion'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ROUTE_URLS } from 'router/routes'

import LanguagesImg from 'shared/img/navIcons/language.png'
import { ReactComponent as LogoutSVG } from 'shared/img/navIcons/log-out.svg'
import ProfileImg from 'shared/img/navIcons/profile.png'

import { useDirection } from 'shared/hooks/useDirection'
import useHideOverflow from 'shared/hooks/useHideOverflow'
import { localeCommonKeysType } from 'shared/locale/en-US'
import { cashbackDaysArray, getNewCashbackTime } from 'shared/utils/cashback'

import { logout } from 'features/auth/actions'
import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import { homeMenuItems } from 'components/Header/data'
import { MenuItem } from 'components/Header/MenuItem/MenuItem'
import { addNotify } from 'components/Notify'
import SetLanguage from 'components/SetLanguage/SetLanguage'
import Button from 'components/UIKit/Button/Button'
import { WheelButton } from 'components/WheelButton'

import { ISideMenuItem, sideMenuItems } from './data'

import './SideMenu.scss'

interface ISideMenuProps {
  isOpen: boolean
  onToggleSideMenu: (val?: boolean) => void
}

const bgVariants = {
  open: {
    opacity: 1,
    '-webkit-backdrop-filter': 'blur(5px)',
    backdropFilter: 'blur(5px)',
    transition: { duration: 0.35 },
  },
  closed: {
    opacity: 0,
    transition: { duration: 0.35 },
    backdropFilter: 'blur(5px)',
    '-webkit-backdrop-filter': 'blur(5px)',
  },
}

const wrapperVariants = {
  open: {
    display: 'flex',
    transition: { duration: 0 },
  },
  closed: {
    display: 'none',
    transition: { when: 'afterChildren' },
  },
}

const b = block('side-menu')

export const SideMenu: React.FC<ISideMenuProps> = ({ isOpen, onToggleSideMenu }) => {
  const dispatch = useDispatch()

  const history = useHistory()
  const { pathname } = useLocation()

  const { pushNewModal } = useModalContext()

  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const localeSlots = useSelector(selectLocaleDict, shallowEqual).slots
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)

  useHideOverflow(isOpen)

  const direction = useDirection()

  const variants = useMemo(
    () => ({
      open: { opacity: 1, x: 0, transition: { duration: 0.35 } },
      closed: {
        opacity: 0,
        x: direction === 'ltr' ? '-100%' : '+100%',
        transition: { duration: 0.35 },
      },
    }),
    [direction],
  )

  const handleClose = useCallback(() => {
    onToggleSideMenu(false)
  }, [onToggleSideMenu])

  const handleLogoutClick = useCallback(() => {
    dispatch(logout())

    handleClose()
  }, [dispatch, handleClose])

  const handleProfileClick = useCallback(() => {
    pushNewModal(MODAL_ROUTE_URLS.PROFILE('tab'))()

    handleClose()
  }, [handleClose])

  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)

  const handleMenuClose = useCallback(() => {
    setOpenMenuIndex(null)
  }, [])

  const handleMenuOpen = useCallback((index: number) => {
    setOpenMenuIndex(index)
  }, [])

  const handleLinkClick = useCallback(
    (link: ISideMenuItem) => (e: React.MouseEvent<HTMLDivElement>) => {
      if (link.authRequired && !isAuth) {
        e.preventDefault()
        addNotify(localeSlots.pleaseAuth)
      }

      if (link.isModal) {
        e.preventDefault()
        pushNewModal(link.route)()
        handleClose()
        return
      }
      history.push(link.route)
      handleClose()
    },
    [handleClose, history, isAuth, localeSlots.pleaseAuth, pushNewModal],
  )

  const itemsList = useMemo(
    () =>
      homeMenuItems.map((item, index) => (
        <MenuItem
          key={item.textID}
          item={item}
          index={index}
          name={item.textID}
          subItems={item.subItems}
          isSideMenu
          callback={handleClose}
          isOpen={openMenuIndex === index}
          onOpen={handleMenuOpen}
          onClose={handleMenuClose}
        />
      )),
    [handleMenuClose, handleMenuOpen, openMenuIndex],
  )

  return (
    <motion.nav
      initial="closed"
      animate={isOpen ? 'open' : 'closed'}
      variants={wrapperVariants}
      className={b('wrapper')}>
      <motion.div className={b()} variants={variants}>
        {itemsList}
        <div className={b('side-menu-group')}>
          {isAuth && (
            <>
              <div className={b('side-menu-item')} onClick={handleProfileClick}>
                {/* <ProfileSVG className={b('side-menu-item-img')} /> */}
                <img src={ProfileImg} alt="Profile" className={b('side-menu-item-img')} />
                <span className={b('side-menu-item-text')}>{locale.profile}</span>
              </div>
            </>
          )}
          <div className={b('divider')} />
          <div>
            <div className={b('side-menu-item')} onClick={pushNewModal(MODAL_ROUTE_URLS.SET_LANGUAGE())}>
              <img src={LanguagesImg} alt="Profile" className={b('side-menu-item-img')} />
              <span className={b('side-menu-item-text')}>{locale.language}</span>
            </div>
          </div>
          {isAuth && (
            <>
              <div className={b('btn-wrapper')}>
                <button onClick={handleLogoutClick} className={b('logout-btn')}>
                  <LogoutSVG /> {locale.logout}
                </button>
              </div>
              {/* <div className={b('btn-wrapper', { isAgentBtn: true })}>
                <AgentLinkButton isStatic />
              </div> */}
            </>
          )}
        </div>
      </motion.div>
      <motion.div onClick={handleClose} className={b('close-block', { open: isOpen })} variants={bgVariants} />
    </motion.nav>
  )
}
